"use client";

import type * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Trash2, Eye } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// Factory for the trailing row-actions column (MoreHorizontal → View/Edit/Delete),
// deduplicated from help-admin / passport-admin / cortex-admin column files.
//
//   const columns = [...dataColumns, createActionsColumn<User>({ onEdit, onDelete })];
//
// Extra product-specific actions go in `items`, each with an optional per-row `disabled`
// predicate — a disabled item stays visible and greyed rather than disappearing per row:
//
//   createActionsColumn<User>({
//     onView,
//     items: [
//       { label: "Resend invite", icon: MailPlus, onSelect: resend,
//         disabled: (u) => u.user_status_id !== UserStatus.Invited },
//     ],
//   });

export interface ActionsColumnItem<T> {
  /** Menu label; also the React key, so keep it unique within one column. */
  label: string;
  /** Invoked with the row when the item is chosen. Never attached while `disabled` returns true. */
  onSelect: (item: T) => void;
  /** Leading icon, rendered like the built-in View/Edit/Delete icons. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Per-row enablement. Omitted ⇒ always enabled. */
  disabled?: (item: T) => boolean;
  /** Renders in the destructive style, like Delete. */
  destructive?: boolean;
}

export interface ActionsColumnOptions<T> {
  onView?: (item: T) => void;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  viewLabel?: string;
  editLabel?: string;
  deleteLabel?: string;
  /** Extra items, rendered between Edit and Delete so the destructive action stays last.
   *  Omitted ⇒ the column renders exactly as before. */
  items?: ActionsColumnItem<T>[];
}

export function createActionsColumn<T>(opts: ActionsColumnOptions<T>): ColumnDef<T> {
  return {
    id: "actions",
    header: "",
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const item = row.original;
      return (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Row actions">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {opts.onView && (
                <DropdownMenuItem onClick={() => opts.onView!(item)}>
                  <Eye className="size-4" />
                  {opts.viewLabel ?? "View"}
                </DropdownMenuItem>
              )}
              {opts.onEdit && (
                <DropdownMenuItem onClick={() => opts.onEdit!(item)}>
                  <Pencil className="size-4" />
                  {opts.editLabel ?? "Edit"}
                </DropdownMenuItem>
              )}
              {opts.items?.map((extra) => {
                const isDisabled = extra.disabled?.(item) ?? false;
                const Icon = extra.icon;
                // The handler is OMITTED (not just `disabled`) when the predicate says so: Radix's
                // pointer-events-none is CSS-only, so a programmatic click on a "disabled" item would
                // still fire onClick. Radix's own aria-disabled + data-disabled remain the non-colour cue.
                return (
                  <DropdownMenuItem
                    key={extra.label}
                    disabled={isDisabled}
                    onClick={isDisabled ? undefined : () => extra.onSelect(item)}
                    className={
                      extra.destructive ? "text-destructive focus:text-destructive" : undefined
                    }
                  >
                    {Icon && <Icon className="size-4" />}
                    {extra.label}
                  </DropdownMenuItem>
                );
              })}
              {opts.onDelete && (
                <DropdownMenuItem
                  onClick={() => opts.onDelete!(item)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="size-4" />
                  {opts.deleteLabel ?? "Delete"}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  };
}
